'use client';

import * as React from 'react';

import type { PlateEditor, PlateElementProps } from 'platejs/react';

import {
  ALargeSmall,
  Heading1Icon,
  Heading2Icon,
  Heading3Icon,
  ListIcon,
  ListOrdered,
  Minus,
  Quote,
} from 'lucide-react';
import { type TComboboxInputElement, KEYS } from 'platejs';
import { PlateElement } from 'platejs/react';

import { insertBlock } from '@/components/editor/transforms';

import {
  InlineCombobox,
  InlineComboboxContent,
  InlineComboboxEmpty,
  InlineComboboxGroup,
  InlineComboboxInput,
  InlineComboboxItem,
} from './inline-combobox';

type SlashItem = {
  icon: React.ReactNode;
  value: string;
  label: string;
  keywords?: string[];
  onSelect: (editor: PlateEditor, value: string) => void;
};

const insertSlashBlock = (editor: PlateEditor, value: string) =>
  insertBlock(editor, value, { upsert: true });

const slashItemGroups: SlashItem[][] = [
  [
    {
      icon: <ALargeSmall />,
      keywords: ['paragraph', 'text', 'p'],
      label: 'Text',
      value: KEYS.p,
      onSelect: insertSlashBlock,
    },
    {
      icon: <Heading1Icon />,
      keywords: ['title', 'h1'],
      label: 'Heading 1',
      value: KEYS.h1,
      onSelect: insertSlashBlock,
    },
    {
      icon: <Heading2Icon />,
      keywords: ['subtitle', 'h2'],
      label: 'Heading 2',
      value: KEYS.h2,
      onSelect: insertSlashBlock,
    },
    {
      icon: <Heading3Icon />,
      keywords: ['subtitle', 'h3'],
      label: 'Heading 3',
      value: KEYS.h3,
      onSelect: insertSlashBlock,
    },
  ],
  [
    {
      icon: <ListIcon />,
      keywords: ['unordered', 'ul', 'bullet', '-'],
      label: 'Bulleted list',
      value: KEYS.ul,
      onSelect: insertSlashBlock,
    },
    {
      icon: <ListOrdered />,
      keywords: ['ordered', 'ol', 'numbered', '1'],
      label: 'Numbered list',
      value: KEYS.ol,
      onSelect: insertSlashBlock,
    },
  ],
  [
    {
      icon: <Quote />,
      keywords: ['citation', 'blockquote', 'quote', '>'],
      label: 'Quote',
      value: KEYS.blockquote,
      onSelect: insertSlashBlock,
    },
    {
      icon: <Minus />,
      keywords: ['divider', 'hr', 'rule', '---'],
      label: 'Divider',
      value: KEYS.hr,
      onSelect: insertSlashBlock,
    },
  ],
];

export function SlashInputElement(props: PlateElementProps<TComboboxInputElement>) {
  const { editor, element } = props;

  return (
    <PlateElement {...props} as="span">
      <InlineCombobox element={element} trigger="/">
        <InlineComboboxInput />

        <InlineComboboxContent>
          <InlineComboboxEmpty>No results</InlineComboboxEmpty>

          {slashItemGroups.map((group, groupIndex) => (
            <InlineComboboxGroup key={groupIndex}>
              {group.map(({ icon, keywords, label, value, onSelect }) => (
                <InlineComboboxItem
                  key={value}
                  value={value}
                  label={label}
                  keywords={keywords}
                  onClick={() => onSelect(editor, value)}
                >
                  <div className="mr-2 text-muted-foreground">{icon}</div>
                  {label}
                </InlineComboboxItem>
              ))}
            </InlineComboboxGroup>
          ))}
        </InlineComboboxContent>
      </InlineCombobox>

      {props.children}
    </PlateElement>
  );
}
